/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 * author : vhe
 * date : 26/10/18 10:31
 */
import { getInfCLogger } from '@infodata-group/inf-commons-core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { EventEmitter, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { NoteBuilder } from '../builder/note.builder';
import { AppConfig } from '../engine/app-config';
import { RestService } from '../engine/http/rest.service';
import { Logger } from '../engine/log/logger';
import { SettingsService } from '../interface/settings-service';
import { Note } from '../model/notification/note';
import { NoteHttpError } from '../model/notification/note-http-error';
import { CookieService } from '../service/cookie.service';
import { NavigationService } from '../service/navigation.service';
import { NotificationService } from '../service/notification.service';
import { WebsocketService } from '../service/websocket.service';
import { PortalAction } from '../tool/action/portal-action';
import { NameValue } from '../tool/name-value';
import { PortalAppFactory } from './portal-app/portal-app-factory';

const logger = getInfCLogger('AuthorizationService');

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  private readonly navigationService = inject(NavigationService);
  router = inject(Router);
  appConfig = inject(AppConfig);
  protected note = inject(NotificationService);
  protected settings = inject(SettingsService);
  protected rest = inject(RestService);
  cookieService = inject(CookieService);
  httpClient = inject(HttpClient);

  private websocketService = inject(WebsocketService);

  get portalAction(): PortalAction {
    if (
      this._portalAction &&
      this._portalAction.applications.filter(
        (app) => app.active && app.id !== 'WI.AUT.CTR',
      ).length === 1
    ) {
      return null;
    }
    return this._portalAction;
  }

  public static PASSWORD_REQUIRED = 'PASSWORD_REQUIRED';
  public isAuthenticated = false;
  public loginRoute = '/login';
  public saveRoute: string;
  public defaultRoute: string;
  public apiEndPoint: string;
  public loginUrl: string;
  public logoutUrl: string;
  public errorRoute: string;
  public user: NameValue;
  public userContext: any;
  protected readonly API_END_POINT = 'apiEndPoint';
  protected readonly WI_AUTH_TOKEN = 'authToken';
  protected readonly WI_AUTH_VALIDITY = 'authValidity';
  public defaultDurationCookiesSeconds = 60 * 60 * 24;
  protected eventEmitter = new EventEmitter<NameValue>();
  protected authToken: string;
  protected redirectUrl = window.location.origin;

  public get token(): string {
    return this.authToken;
  }

  protected _portalAction: PortalAction;
  protected lock: boolean;

  public init() {
    const id = this.cookieService.get('userId');
    if (this.appConfig.apiEndPoint) {
      this.loginUrl = this.appConfig.loginConfig.loginUrl;
      this.loginRoute = this.appConfig.loginConfig.loginRoute;
      this.apiEndPoint = this.appConfig.apiEndPoint;
      this.defaultRoute = this.appConfig.defaultRoute
        ? this.appConfig.defaultRoute
        : '';
      this.logoutUrl = this.appConfig.loginConfig.logoutUrl
        ? this.appConfig.loginConfig.logoutUrl
        : this.appConfig.apiEndPoint + 'logout';
      this.errorRoute = this.appConfig.loginConfig.errorRoute
        ? this.appConfig.loginConfig.errorRoute
        : 'error-logout';
    }
    if (this.appConfig.loginConfig.authorizationEndPoint) {
      this.getAuthToken(id).subscribe(
        (result) => {
          if (result) {
            this.initLogin(id);
          }
        },
        () => {
          this.isAuthenticated = false;
          this.router.navigateByUrl(this.appConfig.loginConfig.loginRoute);
        },
      );
    } else {
      this.initLogin(id);
    }
  }

  public doAuthDefault(
    login: string,
    password: string,
    language: string,
    company?: string,
  ): Observable<boolean> {
    const body: any = {
      usr: login,
      pwd: password,
      lge: language,
      soc: company,
    };
    return this.authenticate(body);
  }

  public connectByNFC(nfc: string): Observable<boolean> {
    const body: any = { extid: nfc };
    return this.authenticate(body);
  }
  /**
   * Store user information on the system
   */
  login(
    token: string,
    userId: string,
    name: string,
    userContext?: any,
    mode = 'default',
  ) {
    this.isAuthenticated = true;
    if (userId) {
      this.user = new NameValue(userId, name);
    } else {
      this.user = new NameValue(token, token);
    }
    if (this.appConfig.loginConfig.authorizationEndPoint) {
      this.getAuthToken(userId).subscribe((result) => {
        if (result) {
          this.finalizeLogin(token, userId, name, userContext, mode);
        }
      });
    } else {
      this.finalizeLogin(token, userId, name, userContext, mode);
    }
  }

  public loadProfile(
    token: string,
    userId: string,
    userName: string,
    context = 'default',
  ) {
    if (this.appConfig.loginConfig.initApi) {
      this.cookieService.set(
        'witoken',
        token,
        this.defaultDurationCookiesSeconds,
        document.domain,
      );
      const apiUrl =
        this.appConfig.apiEndPoint + this.appConfig.loginConfig.initApi;
      this.httpClient.post<any>(apiUrl, null).subscribe({
        next: (result) => {
          if (Object.hasOwn(result, 'user')) {
            this.login(token, result.user, result.name, result, context);
          } else {
            this.login(token, userId, userName, result, context);
          }
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 401) {
            this.note.addNotification(new NoteHttpError(error));
            this.logout();
          }
          return this.rest.catchError(error, 'get', false);
        },
      });
    }
  }

  /**
   * Logout the user properly.
   * Clear all connection cookie
   * @param sendToServer set true to send logout to the server
   * @param navigateTo set path to navigate after logout (by default, navigate to the AppConfig login route)
   */
  logout(sendToServer?: boolean, navigateTo?: string) {
    const context = this.cookieService.get('connectionMode');
    this.websocketService.disconnect();
    if (!sendToServer) {
      this.clear();
    }
    if (!this.logoutContext(context)) {
      if (!navigateTo) {
        const portalUri =
          this.rest.config.loginConfig?.portalConnection?.portalUri;
        navigateTo = portalUri
          ? portalUri + '/logout?redirectURL=' + this.redirectUrl
          : this.loginRoute;
      }
      if (sendToServer) {
        this.sendLogoutToServer(navigateTo);
      } else {
        this.eventEmitter.emit(new NameValue('logout', null));
        this.reload(navigateTo);
      }
    }
  }

  private logoutContext(context: string) {
    if (context === 'azure') {
      const tenant = this.appConfig.loginConfig.azureConnection.tenant;
      const client = this.appConfig.loginConfig.azureConnection.client;
      const redirectUri = encodeURIComponent(
        this.appConfig.loginConfig.azureConnection.logoutUri,
      );
      const route = `https://login.microsoftonline.com/${tenant}/oauth2/logout?response_type=id_token&client_id=${client}\` +
      \`&post_logout_redirect_uri=${redirectUri}&x-client-SKU=Js&x-client-Ver=1.0.0&nonce=56fbb2d7-aa92-497e-821e-f19accd6347a`;
      this.navigationService.goTo(route);
      return true;
    }
    return false;
  }

  private sendLogoutToServer(navigateTo: string) {
    this.httpClient.get(this.logoutUrl, { observe: 'response' }).subscribe({
      next: () => {
        this.reload(navigateTo);
        this.clear();
        this.eventEmitter.emit(new NameValue('logout', null));
      },
      error: () => {
        this.reload(
          this.appConfig.loginConfig.errorRoute ??
            this.appConfig.loginConfig.loginRoute,
        );
        this.clear();
      },
    });
  }

  navigate() {
    if (this.appConfig.loginConfig.redirectAfterLogin) {
      this.navigationService.goTo(this.appConfig.loginConfig.redirection);
    } else {
      if (this.saveRoute) {
        this.router.navigateByUrl(this.saveRoute);
      } else {
        this.router.navigateByUrl(this.defaultRoute);
      }
    }
  }

  /**
   * If user has connected with azure or google, start auto login
   */
  public autoLogin() {
    const connectionMode = this.cookieService.get('connectionMode');
    if (connectionMode === 'azure') {
      this.connectByAzure();
    } else if (connectionMode === 'google') {
      this.connectByGoogle();
    }
  }

  /**
   * Send oauth2 Azure connection
   */
  public connectByAzure() {
    const tenant = this.appConfig.loginConfig.azureConnection.tenant;
    const client = this.appConfig.loginConfig.azureConnection.client;
    const redirectUri = encodeURIComponent(
      this.appConfig.loginConfig.azureConnection.redirectUri,
    );

    const url =
      `https://login.microsoftonline.com/${tenant}/oauth2/authorize?response_type=id_token&client_id=${client}` +
      `&redirect_uri=${redirectUri}&x-client-SKU=Js&x-client-Ver=1.0.0&nonce=56fbb2d7-aa92-497e-821e-f19accd6347a`;

    this.navigationService.goTo(url);
  }

  /**
   * @deprecated
   * Send oauth2 Google connection
   */
  public connectByGoogle() {
    const client = this.appConfig.loginConfig.googleConnection.client;
    const redirectUri = encodeURIComponent(
      this.appConfig.loginConfig.googleConnection.redirectUri,
    );

    const url =
      'https://accounts.google.com/o/oauth2/v2/auth?' +
      'scope=https%3A%2F%2Fwww.googleapis.com%2Fauth%2Fdrive.metadata.readonly&' +
      'include_granted_scopes=true&' +
      'state=state_parameter_passthrough_value&' +
      `redirect_uri=${redirectUri}` +
      '&response_type=token&' +
      `client_id=${client}`;
    this.navigationService.goTo(url);
  }

  public externalLogin$(
    api: string,
    data: unknown,
    context: 'azure' | 'google' | 'portal' | 'witoken',
  ): Observable<unknown> {
    return this.rest.post(api, data, { showLoading: false }).pipe(
      tap((result) => {
        if (!result.witoken) {
          this.logout();
          return;
        }
        if (this.appConfig.loginConfig.initApi) {
          this.loadProfile(result.witoken, result.user, result.name, context);
          return;
        }
        this.login(result.witoken, result.user, result.name, null);
      }),
    );
  }

  /**
   * Check if has cookie connection mode equal to google or azure to start auto-connection
   */
  public hasAutoConnection(): boolean {
    const connectionMode = this.cookieService.get('connectionMode');
    return (
      connectionMode &&
      (connectionMode === 'google' || connectionMode === 'azure')
    );
  }

  /**
   * Check if id is authorized and get appConfig, authorization end date and wi auth token
   */
  public createLinkById(id: string): Observable<boolean> {
    const emitter = new EventEmitter<boolean>();
    this.httpClient
      .post<any>(
        `${this.appConfig.loginConfig.authorizationEndPoint}wiapp-authorization`,
        { 'authorization-id': id },
      )
      .subscribe(
        (result) => {
          emitter.emit(true);
          localStorage.setItem(this.API_END_POINT, result.urlEndpoint);
          this.appConfig.apiEndPoint = result.urlEndpoint;
          this.appConfig.loginConfig.loginUrl = `${result.urlEndpoint}/auth-default`;
          localStorage.setItem(this.WI_AUTH_TOKEN, result.wiauthToken);
          this.router.navigateByUrl(this.appConfig.loginConfig.loginRoute);
        },
        (error: HttpErrorResponse) => {
          this.note.addNotification(
            new NoteBuilder(
              error.status + '',
              'exclamation-triangle danger',
              error.statusText,
            )
              .setAlertNote()
              .build(),
          );
          emitter.error(error);
        },
      );
    return emitter.asObservable();
  }

  public subscribe(next: (NameValue) => void, error?: (any) => void) {
    this.eventEmitter.subscribe(next, error);
  }

  protected finalizeLogin(
    token: string,
    userId: string,
    name: string,
    userContext?: any,
    mode = 'default',
  ) {
    this.cookieService.set(
      'witoken',
      token,
      this.defaultDurationCookiesSeconds,
      document.domain,
    );
    this.cookieService.set(
      'userId',
      userId,
      this.defaultDurationCookiesSeconds,
      document.domain,
    );
    this.cookieService.set(
      'userName',
      name,
      this.defaultDurationCookiesSeconds,
      document.domain,
    );
    this.cookieService.set(
      'connectionMode',
      mode,
      this.defaultDurationCookiesSeconds,
      document.domain,
    );
    if (userContext) {
      localStorage.setItem('userContext', JSON.stringify(userContext));
      this.userContext = userContext;
    }
    Logger.userContext = userContext;
    Logger.token = token;
    this.eventEmitter.emit(new NameValue('login', this.user));
    this.navigate();
  }

  protected finalizeAuthDefault(
    requestBody: any,
    response: HttpResponse<object>,
  ) {
    const token = response.headers.get('x-witoken');
    if (response.body['code'] === AuthorizationService.PASSWORD_REQUIRED) {
      const note = new NoteBuilder(
        'authentication.login.security',
        'lock',
        response.body['message'],
      )
        .setAlertNote()
        .build();
      this.note.addNotification(note);
    } else {
      const id = response.body['user']
        ? response.body['user']
        : requestBody.usr;
      if (this.appConfig.loginConfig.initApi) {
        this.loadProfile(token, response.body['user'], response.body['name']);
      } else {
        this.login(
          response.body['witoken'],
          id,
          response.body['name'],
          response.body,
        );
      }
    }
  }

  protected initLogin(id: string) {
    this.isAuthenticated = this.cookieService.check('witoken');
    if (this.isAuthenticated) {
      try {
        const context = JSON.parse(localStorage.getItem('userContext'));
        if (!context && this.appConfig.loginConfig.initApi) {
          this.loadProfile(
            this.cookieService.get('witoken'),
            this.cookieService.get('userId'),
            this.cookieService.get('userName'),
          );
          return;
        } else {
          this.userContext = context;
        }
        this.user = new NameValue(id, this.cookieService.get('userName'));
        this.eventEmitter.emit(new NameValue('login', this.user));
      } catch (e: any) {
        logger.error('Failed to restore the session', e);
        this.logout();
        return;
      }
    } else {
      this.logout();
    }
  }

  protected getAuthToken(userId: string): Observable<string> {
    const emitter = new BehaviorSubject<string>(null);
    const authToken = this.cookieService.get(this.WI_AUTH_VALIDITY);
    if (!authToken) {
      this.authToken = localStorage.getItem(this.WI_AUTH_TOKEN);
      if (userId) {
        const url = `${this.appConfig.loginConfig.authorizationEndPoint}wiapp-authorization-renew?
        WITOKEN=${this.authToken}&USERNAME=${userId}`;
        this.httpClient.get<any>(url).subscribe(
          (result) => {
            if (result && result.authorizationNbrDay !== undefined) {
              this.cookieService.set(
                this.WI_AUTH_VALIDITY,
                this.authToken,
                result.authorizationNbrDay * (60 * 60 * 24),
                document.domain,
              );
              emitter.next(this.authToken);
            }
          },
          (error) => {
            this.note.addNotification(
              new Note(
                'Error',
                'exclamation-triangle danger',
                `(${error.status}) ${error.statusText}`,
              ),
            );
            emitter.error(error);
          },
        );
      }
    } else {
      emitter.next(authToken);
    }
    return emitter.asObservable();
  }

  protected catchInvalidCookie() {
    this.cookieService.clear(document.domain);
    localStorage.clear();
    this.cookieService.clear();
    this.logout();
  }

  public clear(): void {
    this.cookieService.clear(document.domain);
    localStorage.clear();
    sessionStorage.clear();
    this.settings.clear();
    this.isAuthenticated = false;
    this.user = null;
    this.userContext = null;
  }

  protected reload(route: string) {
    if (route.indexOf('http') === 0) {
      this.navigationService.goTo(route);
    } else {
      this.router.navigateByUrl(route).then();
    }
  }

  public generatePortalAction() {
    if (this.lock) {
      return;
    }
    this.lock = true;
    this.rest
      .getAll(
        new PortalAppFactory(
          this.appConfig.loginConfig.portalConnection.userAppsApi,
        ),
      )
      .pipe(finalize(() => (this.lock = false)))
      .subscribe((apps) => {
        this._portalAction = new PortalAction(apps);
        this.lock = false;
        this.eventEmitter.emit(
          new NameValue('portal-action', this._portalAction),
        );
      });
  }

  public sendResetRequest$(mail: string): Observable<string> {
    const url = this.appConfig.loginConfig.resetRequest
      ? this.appConfig.loginConfig.resetRequest
      : 'reset-request';
    return this.rest.post(url, { mail }, { isSilent: true }).pipe(
      map(() => {
        return mail;
      }),
    );
  }

  protected authenticate(body: any): Observable<boolean> {
    const subject = new Subject<boolean>();
    this.httpClient
      .post(this.appConfig.loginConfig.loginUrl, body, {
        observe: 'response',
        withCredentials: true,
      })
      .subscribe({
        next: (response) => {
          if (
            response.body['code'] === AuthorizationService.PASSWORD_REQUIRED
          ) {
            subject.error(response);
          } else {
            subject.next(true);
          }
          subject.complete();
          this.finalizeAuthDefault(body, response);
        },
        error: (error: HttpErrorResponse) => {
          subject.error(error);
          subject.complete();
        },
      });
    return subject.asObservable();
  }
}
